import { Pressable, Text, View, type ViewStyle } from "react-native";
import { colors, semanticColors, typography } from "../theme/tokens";
import { getInteractionDelta } from "../utils/interactionStyles";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * Circular radio with optional inline label. Audit task 2.5.
 *
 * Inline alternative to MoonSelectField when the option set is small (2–4)
 * and the visual weight of a modal picker isn't warranted. Consumers
 * compose multiple instances inside a parent that owns the group selection
 * — no `RadioGroup` context primitive here, since a single-line `selected
 * === option.value` check is simpler than a context dance.
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle / selected — outer border + inner dot
 *   - disabled        — opacity 0.5, presses ignored
 *   - error / focused / loading — owned by MoonFormField if needed.
 */
type Props = {
  value: boolean;
  onChange: () => void;
  label?: string;
  /** Hue used for the selected ring + dot. Defaults to warm. */
  accentColor?: string;
  disabled?: boolean;
  /**
   * Stable analytics identifier. The selected value is never captured — a
   * radio can record a clinical fact.
   */
  action?: string;
  testID?: string;
  style?: ViewStyle;
};

const OUTER_SIZE = 20;
const INNER_SIZE = 10;

export function MoonRadio({
  value,
  onChange,
  label,
  accentColor = colors.warm,
  disabled,
  action,
  testID,
  style,
}: Props) {
  const capturePress = usePressCapture("MoonRadio");
  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityState={{ checked: value, disabled }}
      aria-checked={value}
      aria-disabled={disabled}
      onPress={() => {
        capturePress(pressLabel(label), action);
        onChange();
      }}
      disabled={disabled}
      testID={testID}
    >
      {({ hovered, focused }) => (
        <View
          style={[
            {
              flexDirection: "row",
              alignItems: "center",
              opacity: disabled ? 0.5 : 1,
            },
            style,
            getInteractionDelta({ hovered, focused }),
          ]}
        >
          <View
            style={{
              width: OUTER_SIZE,
              height: OUTER_SIZE,
              borderRadius: OUTER_SIZE / 2,
              borderWidth: 1.5,
              borderColor: value ? accentColor : semanticColors.border.subtle,
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {value ? (
              <View
                style={{
                  width: INNER_SIZE,
                  height: INNER_SIZE,
                  borderRadius: INNER_SIZE / 2,
                  backgroundColor: accentColor,
                }}
              />
            ) : null}
          </View>
          {label ? (
            <Text
              style={{
                fontFamily: typography.families.sans,
                fontSize: 14,
                color: colors.text,
                flexShrink: 1,
                marginLeft: 10,
              }}
            >
              {label}
            </Text>
          ) : null}
        </View>
      )}
    </Pressable>
  );
}
